import * as Sentry from '@sentry/vue';

import { ModuleSocketState, State } from '@/models/main';

import { ActionContext } from 'vuex';
import Vue from 'vue';
import store from '@/store';

const reconnectLimit = 5;
const reconnectTimeOutMin = 3000;
const reconnectTimeOutMax = 15000;

export const moduleSocket = {
  namespaced: true,
  state: {
    socket: {
      isConnected: false,
      isAuthenticated: false,
      message: '',
      reconnectError: false,
      reconnectAttempts: 0,
    },
  },
  getters: {
    isConnected: (state: ModuleSocketState): boolean => state.socket.isConnected,
  },
  mutations: {
    SOCKET_ONOPEN(state: ModuleSocketState): void {
      state.socket.isConnected = true;
      state.socket.reconnectAttempts = 0;
    },
    SOCKET_ON_AUTH_MESSAGE(state: ModuleSocketState, value: boolean): void {
      state.socket.isAuthenticated = value;
    },
    SOCKET_ONMESSAGE(state: ModuleSocketState, message: string): void {
      state.socket.message = message;
    },
    SOCKET_ONCLOSE(state: ModuleSocketState): void {
      state.socket.isConnected = false;
      state.socket.isAuthenticated = false;
    },
    // SOCKET_ONERROR(state: ModuleSocketState, event) {
    //   console.error(state, event);
    // },
    // mutations for reconnect methods
    SOCKET_RECONNECT(state) {
      state.socket.reconnectAttempts += 1;
    },
    SOCKET_RECONNECT_ERROR(state: ModuleSocketState): void {
      state.socket.reconnectError = true;
    },
  },
  actions: {
    // Socket events:

    // Set global socket instance & listeners on connect
    socketConnect(): void {
      const socketURL = process.env.VUE_APP_WEBSOCKET;
      if (socketURL) {
        if (Vue.prototype.$socket && Vue.prototype.$socket.readyState === WebSocket.OPEN) {
          return; // If socket is already open
        }
        // Set global socket instance:
        Vue.prototype.$socket = new WebSocket(socketURL);
        // Set listeners:
        // Socket opened:
        Vue.prototype.$socket.onopen = function (event) {
          store.dispatch('moduleSocket/socketOpen', event);
        };
        // Message:
        Vue.prototype.$socket.onmessage = function (event) {
          store.dispatch('moduleSocket/socketOnMessage', event);
        };
        // Socket closed:
        Vue.prototype.$socket.onclose = function (event) {
          store.dispatch('moduleSocket/socketOnClose', event);
        };
        // Socket error:
        Vue.prototype.$socket.onerror = function (event) {
          store.dispatch('moduleSocket/socketOnError', event);
        };
      }
    },

    // Disconnect from the socket
    async socketDisconnect(): Promise<void> {
      if (Vue.prototype.$socket) {
        await Vue.prototype.$socket.close(1000);
        delete Vue.prototype.$socket;
      }
    },

    // Send auth token when socket opened
    socketOpen({ commit }: ActionContext<ModuleSocketState, State>, event: Event): void {
      // Get token from authData module
      const authData = store.getters['moduleAuthData/authData'];
      const msg = {
        type: 'auth',
        method_payload: authData.access,
      };
      // Send auth msg to the socket:
      Vue.prototype.$socket.send(JSON.stringify(msg));
      // Set isConnected = true
      commit('SOCKET_ONOPEN', event);
    },

    // Socket close:
    socketOnClose({ state, commit }: ActionContext<ModuleSocketState, State>): void {
      if (state.socket.isAuthenticated || state.socket.reconnectError) {
        return;
      }
      store.dispatch('moduleSocket/reconnectSocket');
      commit('SOCKET_ONCLOSE');
    },

    socketOnError({ state }: ActionContext<ModuleSocketState, State>, event: Event): void {
      // Checks for the absence of a reconnect error and verifies the WebSocket state.
      // If there is no error and the WebSocket is not open, it triggers a reconnection attempt
      if (
        !state.socket.reconnectError &&
        (!Vue.prototype.$socket || Vue.prototype.$socket.readyState !== WebSocket.OPEN)
      ) {
        store.dispatch('moduleSocket/reconnectSocket');
      }

      // Create an error object and send it to Sentry.
      const error = new Error('WebSocket error occurred');
      Sentry.withScope(scope => {
        scope.setLevel('error');
        scope.setExtra('event', event);
        scope.setContext('WebSocket', {
          eventType: event.type,
          readyState: Vue.prototype.$socket?.readyState,
          reconnectAttempts: state.socket.reconnectAttempts,
          url: Vue.prototype.$socket?.url,
          timeStamp: event.timeStamp,
          message: event instanceof MessageEvent ? event.data : 'No message data',
        });
        Sentry.captureException(error);
      });
    },

    // Socket got msg
    socketOnMessage({ commit }: ActionContext<ModuleSocketState, State>, message: MessageEvent): void {
      const isConnected = store.getters['moduleSocket/isConnected'];

      if (isConnected) {
        // Set message to the store
        commit('SOCKET_ONMESSAGE', message);
        // Parse it
        const msgData = JSON.parse(message.data);
        //
        // Message cases:
        //
        // Got Auth msg:
        if (msgData.info === 'authentication was successful') {
          // Set isAuthenticated = true
          commit('SOCKET_ON_AUTH_MESSAGE', true);
          // Run notification pipeline
          store.dispatch('moduleNotifications/getNotificationsAll');
        }
        //
        // Got new notifications
        if (msgData.action === 'update_notifications') {
          // Run notification pipeline
          store.dispatch('moduleNotifications/getNotificationsAll');
        }
      }
    },

    /**
     * Reconnects the socket if it's not connected and the number of reconnect attempts is less than 5.
     * If the number of reconnect attempts is 5 or more, sets the reconnectError flag to true.
     * @param { state, commit }
     */
    reconnectSocket({ state, commit }: ActionContext<ModuleSocketState, State>): void {
      if (state.socket.reconnectAttempts < reconnectLimit) {
        commit('SOCKET_RECONNECT');
        // This code calculates the progressive retry delay for socket reconnection,
        // After the delay, it dispatches an action to connect the socket
        const retryDelay = Math.min(reconnectTimeOutMin * state.socket.reconnectAttempts, reconnectTimeOutMax);
        setTimeout(() => {
          store.dispatch('moduleSocket/socketConnect');
        }, retryDelay);
      } else {
        // Set the reconnectError flag to true if the number of reconnect attempts is 5 or more
        commit('SOCKET_RECONNECT_ERROR');
      }
    },

    // Send message to the socket
    // socketSendMessage({ commit }: ActionContext<ModuleSocketState, State>, message: MessageEvent): void {
    //   Vue.prototype.$socket.send(message);
    // },
  },
};
